// ═══════════════════════════════════════════════════════════════════════════
// Living Engine Design Tokens
// packages/@forgetrack/engine/src/tokens.ts
//
// Canonical home for the Living Engine palette, fonts, and keyframes used by
// every Edson app (ForgeTrack, ForgeRadar, ...). Lifted from
// apps/web/src/components/engine/tokens.ts on 2026-04-24.
// ═══════════════════════════════════════════════════════════════════════════

export const ENGINE_COLORS = {
  // Accent palette — semantic meaning per color
  neon: '#34d399',    // Primary engine green — success, active, live
  cyan: '#22d3ee',    // Simulation, data, connectivity
  violet: '#a78bfa',  // Analysis, intelligence, deep thinking
  amber: '#fbbf24',   // Urgency, warnings, attention required
  coral: '#f97066',   // Danger, risk, negative impact
  blue: '#60a5fa',    // Timeline, scheduling, neutral data
  pink: '#f472b6',    // Rarely used — accent pops

  // Text hierarchy — bumped 2026.4.7 for WCAG AA contrast on the dark surface.
  text: '#e8ecf2',
  textSoft: 'rgba(255,255,255,0.85)',
  muted: 'rgba(255,255,255,0.78)',
  dim: 'rgba(255,255,255,0.62)',
  faint: 'rgba(255,255,255,0.18)',

  // Surfaces — layered depth
  bg: '#050910',
  surface: 'rgba(8,16,32,0.88)',
  panel: 'rgba(12,22,44,0.92)',
  cardHover: 'rgba(16,28,52,0.95)',

  // Glow variants
  neonGlow: 'rgba(52,211,153,0.6)',
  cyanGlow: 'rgba(34,211,238,0.5)',
  violetGlow: 'rgba(167,139,250,0.5)',
  amberGlow: 'rgba(251,191,36,0.5)',
  coralGlow: 'rgba(249,112,102,0.5)',
} as const;

export const ENGINE_FONTS = {
  display: "'Outfit', system-ui, -apple-system, sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', 'SF Mono', monospace",
} as const;

export const ENGINE_KEYFRAMES = `
@keyframes engine-neural {
  0% { background-position: 0 0; }
  100% { background-position: 60px 60px; }
}
@keyframes engine-scan {
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100vh); }
}
@keyframes engine-core-pulse {
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.08); opacity: 1; }
}
@keyframes engine-ping {
  0% { transform: scale(1); opacity: 0.8; }
  75%, 100% { transform: scale(2.5); opacity: 0; }
}
@keyframes engine-feed-in {
  0% { opacity: 0; transform: translateX(-12px); }
  100% { opacity: 1; transform: translateX(0); }
}
@keyframes engine-icon-pop {
  0% { transform: scale(0); opacity: 0; }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes engine-icon-pulse {
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
}
@keyframes engine-tooltip-in {
  0% { opacity: 0; transform: translateY(4px) scale(0.97); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes engine-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes engine-bar-fill {
  0% { width: 0%; }
}
@keyframes engine-count-flash {
  0% { text-shadow: 0 0 20px currentColor; }
  100% { text-shadow: 0 0 0 transparent; }
}
`;

export type EngineColor = keyof typeof ENGINE_COLORS;
export { ENGINE_COLORS as C, ENGINE_FONTS as F, ENGINE_KEYFRAMES as K };
